/**
 * SVG Puzzle & Visual Stimulus Builder for Kazakan Cognitive Test
 * Pure vector graphics, scalable, responsive, zero raster images.
 */

export const SVG_COLORS = {
  primary: '#3b82f6',
  primaryDark: '#1d4ed8',
  accent: '#38bdf8',
  text: 'currentColor',
  textMuted: '#94a3b8',
  bgCard: 'rgba(255, 255, 255, 0.04)',
  border: 'rgba(255, 255, 255, 0.15)',
  fillDefault: '#3b82f6',
  strokeDefault: 'currentColor'
};

export function escapeAttr(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  })[c]);
}

/**
 * Creates an SVG wrapper element string with viewBox and attributes
 */
export function wrapSvg(content, viewBox = '0 0 260 260', className = 'iq-svg-puzzle', ariaLabel = 'Puzzle Diagram') {
  return `<svg class="${escapeAttr(className)}" viewBox="${escapeAttr(viewBox)}" xmlns="http://www.w3.org/2000/svg" role="img" aria-label="${escapeAttr(ariaLabel)}">${content}</svg>`;
}

/**
 * Renders a 3x3 Matrix Grid for pattern deduction
 * cells: array of 9 items, where 8 are shape descriptor functions and the 9th is '?'
 */
export function render3x3Matrix(cells, size = 300) {
  const cellSize = Math.floor((size - 24) / 3);
  const gap = 8;
  const offset = 4;
  let out = '';

  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const idx = r * 3 + c;
      const x = offset + c * (cellSize + gap);
      const y = offset + r * (cellSize + gap);
      const isMissing = idx === 8;

      out += `<rect x="${x}" y="${y}" width="${cellSize}" height="${cellSize}" rx="10" class="${isMissing ? 'matrix-cell-missing' : 'matrix-cell'}"/>`;

      const cx = x + cellSize / 2;
      const cy = y + cellSize / 2;

      if (isMissing) {
        out += `<text x="${cx}" y="${cy + 10}" text-anchor="middle" class="matrix-q-mark">?</text>`;
      } else if (typeof cells[idx] === 'function') {
        out += `<g transform="translate(${cx}, ${cy})">${cells[idx](cellSize * 0.78)}</g>`;
      } else if (typeof cells[idx] === 'string') {
        out += `<g transform="translate(${cx}, ${cy})">${cells[idx]}</g>`;
      }
    }
  }

  return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-matrix', '3 by 3 Logic Matrix');
}

/**
 * Render a single option card SVG
 */
export function renderOptionSvg(drawFn, size = 110, ariaLabel = 'Option') {
  const content = `<g transform="translate(${size / 2}, ${size / 2})">${drawFn(size * 0.82)}</g>`;
  return wrapSvg(content, `0 0 ${size} ${size}`, 'iq-svg-option', ariaLabel);
}

/**
 * Shape drawer helpers centered at (0, 0) with target size radius
 */
export const DrawShapes = {
  circle(r, filled = false, stroke = 'currentColor', fill = '#83C8F0') {
    const radius = Math.max(4, r / 2);
    return `<circle cx="0" cy="0" r="${radius}" fill="${filled ? fill : 'none'}" stroke="${stroke}" stroke-width="2.4"/>`;
  },

  square(s, filled = false, stroke = 'currentColor', fill = '#83C8F0') {
    const half = Math.max(4, s / 2);
    return `<rect x="${-half}" y="${-half}" width="${s}" height="${s}" rx="4" fill="${filled ? fill : 'none'}" stroke="${stroke}" stroke-width="2.4"/>`;
  },

  triangle(s, filled = false, stroke = 'currentColor', fill = '#83C8F0', rot = 0) {
    const h = s * 0.866;
    const pts = `0,${-h * 0.6} ${s / 2},${h * 0.4} ${-s / 2},${h * 0.4}`;
    return `<polygon points="${pts}" fill="${filled ? fill : 'none'}" stroke="${stroke}" stroke-width="2.4" transform="rotate(${rot})"/>`;
  },

  diamond(s, filled = false, stroke = 'currentColor', fill = '#83C8F0') {
    const d = s * 0.55;
    const pts = `0,${-d} ${d},0 0,${d} ${-d},0`;
    return `<polygon points="${pts}" fill="${filled ? fill : 'none'}" stroke="${stroke}" stroke-width="2.4"/>`;
  },

  star(s, filled = false, stroke = 'currentColor', fill = '#83C8F0') {
    const points = [];
    const rOuter = s * 0.52;
    const rInner = s * 0.24;
    for (let i = 0; i < 10; i++) {
      const r = i % 2 === 0 ? rOuter : rInner;
      const angle = (i * Math.PI) / 5 - Math.PI / 2;
      points.push(`${(Math.cos(angle) * r).toFixed(1)},${(Math.sin(angle) * r).toFixed(1)}`);
    }
    return `<polygon points="${points.join(' ')}" fill="${filled ? fill : 'none'}" stroke="${stroke}" stroke-width="2.2"/>`;
  },

  cross(s, stroke = 'currentColor', width = 2.8) {
    const h = s * 0.44;
    return `<line x1="${-h}" y1="0" x2="${h}" y2="0" stroke="${stroke}" stroke-width="${width}" stroke-linecap="round"/><line x1="0" y1="${-h}" x2="0" y2="${h}" stroke="${stroke}" stroke-width="${width}" stroke-linecap="round"/>`;
  },

  dots(count, s, filled = true, color = '#83C8F0') {
    if (!count || count <= 0) return '';
    const strokeAttr = 'stroke="rgba(255, 255, 255, 0.3)" stroke-width="1.2"';
    const makeDot = (x, y, r) =>
      `<circle cx="${x.toFixed(1)}" cy="${y.toFixed(1)}" r="${r.toFixed(1)}" fill="${color}" ${strokeAttr}/>`;

    if (count === 1) {
      const r = Math.max(4.2, s * 0.12);
      return makeDot(0, 0, r);
    }
    if (count === 2) {
      const d = s * 0.24;
      const r = Math.max(3.8, s * 0.095);
      return makeDot(-d, 0, r) + makeDot(d, 0, r);
    }
    if (count === 3) {
      const d = s * 0.24;
      const r = Math.max(3.6, s * 0.09);
      return makeDot(0, -d * 0.9, r) + makeDot(-d, d * 0.8, r) + makeDot(d, d * 0.8, r);
    }
    if (count === 4) {
      const d = s * 0.22;
      const r = Math.max(3.5, s * 0.085);
      return makeDot(-d, -d, r) + makeDot(d, -d, r) + makeDot(-d, d, r) + makeDot(d, d, r);
    }
    if (count === 5) {
      const d = s * 0.24;
      const r = Math.max(3.4, s * 0.08);
      return makeDot(-d, -d, r) + makeDot(d, -d, r) + makeDot(0, 0, r * 1.05) + makeDot(-d, d, r) + makeDot(d, d, r);
    }
    if (count === 6) {
      const dx = s * 0.24;
      const dy = s * 0.20;
      const r = Math.max(3.3, s * 0.076);
      return makeDot(-dx, -dy, r) + makeDot(0, -dy, r) + makeDot(dx, -dy, r) +
             makeDot(-dx, dy, r) + makeDot(0, dy, r) + makeDot(dx, dy, r);
    }
    if (count === 7) {
      const dx = s * 0.24;
      const dy = s * 0.22;
      const r = Math.max(3.2, s * 0.072);
      return makeDot(-dx, -dy, r) + makeDot(0, -dy, r) + makeDot(dx, -dy, r) +
             makeDot(0, 0, r * 1.05) +
             makeDot(-dx, dy, r) + makeDot(0, dy, r) + makeDot(dx, dy, r);
    }
    if (count === 8) {
      const dx = s * 0.24;
      const dy = s * 0.22;
      const r = Math.max(3.0, s * 0.068);
      return makeDot(-dx, -dy, r) + makeDot(0, -dy, r) + makeDot(dx, -dy, r) +
             makeDot(-dx, 0, r) + makeDot(dx, 0, r) +
             makeDot(-dx, dy, r) + makeDot(0, dy, r) + makeDot(dx, dy, r);
    }
    if (count === 9) {
      const d = s * 0.24;
      const r = Math.max(2.8, s * 0.065);
      let out = '';
      for (const y of [-d, 0, d]) {
        for (const x of [-d, 0, d]) {
          out += makeDot(x, y, r);
        }
      }
      return out;
    }

    // Dynamic symmetrical matrix grid for counts >= 10
    const cols = Math.ceil(Math.sqrt(count));
    const rows = Math.ceil(count / cols);
    const r = Math.max(2.4, Math.min(s * 0.065, (s * 0.72) / (Math.max(cols, rows) * 2.8)));
    const spanX = s * 0.65;
    const spanY = s * 0.65;
    const stepX = cols > 1 ? spanX / (cols - 1) : 0;
    const stepY = rows > 1 ? spanY / (rows - 1) : 0;
    const startX = -spanX / 2;
    const startY = -spanY / 2;

    let out = '';
    for (let i = 0; i < count; i++) {
      const row = Math.floor(i / cols);
      const col = i % cols;
      const x = cols > 1 ? startX + col * stepX : 0;
      const y = rows > 1 ? startY + row * stepY : 0;
      out += makeDot(x, y, r);
    }
    return out;
  },

  pieSlices(s, activeSliceIndex, totalSlices = 4, color = '#83C8F0') {
    const r = s * 0.44;
    let out = `<circle cx="0" cy="0" r="${r}" fill="none" stroke="currentColor" stroke-width="2.2"/>`;
    for (let i = 0; i < totalSlices; i++) {
      const a = (i * 2 * Math.PI) / totalSlices;
      out += `<line x1="0" y1="0" x2="${(Math.cos(a) * r).toFixed(1)}" y2="${(Math.sin(a) * r).toFixed(1)}" stroke="currentColor" stroke-opacity="0.35" stroke-width="1.5"/>`;
    }
    const aStart = (activeSliceIndex * 2 * Math.PI) / totalSlices;
    const aEnd = ((activeSliceIndex + 1) * 2 * Math.PI) / totalSlices;
    const x1 = (Math.cos(aStart) * r).toFixed(1);
    const y1 = (Math.sin(aStart) * r).toFixed(1);
    const x2 = (Math.cos(aEnd) * r).toFixed(1);
    const y2 = (Math.sin(aEnd) * r).toFixed(1);
    const largeArc = (aEnd - aStart) > Math.PI ? 1 : 0;
    out += `<path d="M 0 0 L ${x1} ${y1} A ${r} ${r} 0 ${largeArc} 1 ${x2} ${y2} Z" fill="${color}" stroke="${color}" stroke-width="2.2"/>`;
    return out;
  },

  arrow(s, rot = 0, color = '#83C8F0') {
    const len = s * 0.42;
    const head = s * 0.22;
    const pts = `0,${-len - 3} ${head * 0.6},${-len + head} ${-head * 0.6},${-len + head}`;
    return `<g transform="rotate(${rot})"><line x1="0" y1="${len}" x2="0" y2="${-len}" stroke="${color}" stroke-width="3.2" stroke-linecap="round"/><polygon points="${pts}" fill="${color}"/></g>`;
  }
};

/**
 * Visual series progression (4 items shown horizontally, 5th item is '?')
 */
export function renderHorizontalSeries(items, size = 360) {
  const count = 5;
  const gap = 10;
  const cellW = Math.floor((size - (count - 1) * gap) / count);
  const cellH = cellW;
  let out = '';

  for (let i = 0; i < count; i++) {
    const x = i * (cellW + gap);
    const y = 0;
    const isMissing = i === 4;

    out += `<rect x="${x}" y="${y}" width="${cellW}" height="${cellH}" rx="10" class="${isMissing ? 'series-cell-missing' : 'series-cell'}"/>`;

    const cx = x + cellW / 2;
    const cy = y + cellH / 2;

    if (isMissing) {
      out += `<text x="${cx}" y="${cy + 9}" text-anchor="middle" class="series-q-mark">?</text>`;
    } else if (typeof items[i] === 'function') {
      out += `<g transform="translate(${cx}, ${cy})">${items[i](cellW * 0.76)}</g>`;
    } else if (typeof items[i] === 'string') {
      out += `<g transform="translate(${cx}, ${cy})">${items[i]}</g>`;
    }
  }

  return wrapSvg(out, `0 0 ${size} ${cellH}`, 'iq-svg-series', 'Sequential Shape Series');
}

/**
 * Cube Folding Net Diagram
 */
export function renderCubeNet(faceSymbols, size = 220) {
  const s = Math.floor(size / 4.2);
  const startX = Math.floor(size / 2 - s * 1.5);
  const startY = Math.floor(size / 2 - s * 2);

  const positions = [
    { c: 1, r: 0 },
    { c: 0, r: 1 },
    { c: 1, r: 1 },
    { c: 2, r: 1 },
    { c: 1, r: 2 },
    { c: 1, r: 3 }
  ];

  let out = '';

  positions.forEach((pos, idx) => {
    const x = startX + pos.c * s;
    const y = startY + pos.r * s;
    out += `<rect x="${x}" y="${y}" width="${s}" height="${s}" rx="6" class="net-cell"/>`;
    const cx = x + s / 2;
    const cy = y + s / 2;
    const sym = faceSymbols[idx];
    if (typeof sym === 'function') {
      out += `<g transform="translate(${cx}, ${cy})">${sym(s * 0.72)}</g>`;
    } else {
      out += `<text x="${cx}" y="${cy + 8}" text-anchor="middle" font-family="system-ui, sans-serif" font-size="${Math.floor(s * 0.58)}" font-weight="800" fill="currentColor">${sym ?? ''}</text>`;
    }
  });

  return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-net', 'Unfolded Cube Net');
}

/**
 * 3D Isometric Cube Drawing with 3 visible faces: Top, Left, Right
 * Generous sizing with bold, enlarged face symbols for high readability
 */
export function renderIsometricCube(topSym, leftSym, rightSym, size = 110) {
  const cx = size / 2;
  const cy = size / 2;
  const r = size * 0.44;
  const sin30 = 0.5;
  const cos30 = 0.866;

  const pTop = `${cx},${cy - r}`;
  const pTopR = `${cx + r * cos30},${cy - r * sin30}`;
  const pCenter = `${cx},${cy}`;
  const pTopL = `${cx - r * cos30},${cy - r * sin30}`;
  const pBottomR = `${cx + r * cos30},${cy + r * sin30}`;
  const pBottom = `${cx},${cy + r}`;
  const pBottomL = `${cx - r * cos30},${cy + r * sin30}`;

  let out = '';
  // Top Face (lighter tint)
  out += `<polygon points="${pTop} ${pTopR} ${pCenter} ${pTopL}" class="cube-face-top" stroke="#38bdf8" stroke-width="2.2"/>`;
  // Left Face (darker shade)
  out += `<polygon points="${pTopL} ${pCenter} ${pBottom} ${pBottomL}" class="cube-face-left" stroke="#3b82f6" stroke-width="2.2"/>`;
  // Right Face (medium shade)
  out += `<polygon points="${pCenter} ${pTopR} ${pBottomR} ${pBottom}" class="cube-face-right" stroke="#6666E8" stroke-width="2.2"/>`;

  // Draw large, clear symbols on faces
  const topC = { x: cx, y: cy - r * sin30 };
  const leftC = { x: cx - r * cos30 * 0.5, y: cy + r * sin30 * 0.5 };
  const rightC = { x: cx + r * cos30 * 0.5, y: cy + r * sin30 * 0.5 };

  const drawFace = (sym, pt, scale = 0.62) => {
    if (typeof sym === 'function') {
      return `<g transform="translate(${pt.x}, ${pt.y})">${sym(r * scale)}</g>`;
    }
    return `<text x="${pt.x}" y="${pt.y + Math.floor(r * scale * 0.35)}" text-anchor="middle" font-family="system-ui, sans-serif" font-size="${Math.floor(r * scale * 1.35)}" font-weight="800" fill="currentColor">${sym ?? ''}</text>`;
  };

  out += drawFace(topSym, topC);
  out += drawFace(leftSym, leftC);
  out += drawFace(rightSym, rightC);

  return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-isometric', 'Folded Cube Perspective');
}

/**
 * Radar / Spider chart for Results profile
 */
export function renderRadarChart(domainsData, size = 320) {
  const cx = size / 2;
  const cy = size / 2;
  const radius = size * 0.36;
  const n = domainsData.length;
  if (n < 3) return '';

  let out = `<rect x="0" y="0" width="${size}" height="${size}" rx="16" class="radar-board"/>`;

  // Draw concentric polygons (25%, 50%, 75%, 100%)
  [0.25, 0.5, 0.75, 1.0].forEach((level) => {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
      const x = cx + Math.cos(angle) * radius * level;
      const y = cy + Math.sin(angle) * radius * level;
      pts.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    out += `<polygon points="${pts.join(' ')}" class="radar-ring" fill="${level === 1 ? 'rgba(59, 130, 246, 0.05)' : 'none'}"/>`;
  });

  // Spokes & labels
  const dataPoints = [];
  domainsData.forEach((d, i) => {
    const angle = (i * 2 * Math.PI) / n - Math.PI / 2;
    const xEnd = cx + Math.cos(angle) * radius;
    const yEnd = cy + Math.sin(angle) * radius;
    out += `<line x1="${cx}" y1="${cy}" x2="${xEnd.toFixed(1)}" y2="${yEnd.toFixed(1)}" class="radar-spoke"/>`;

    const scorePct = Math.max(0.1, Math.min(1.0, (d.score || 0) / (d.max || 4)));
    const dx = cx + Math.cos(angle) * radius * scorePct;
    const dy = cy + Math.sin(angle) * radius * scorePct;
    dataPoints.push(`${dx.toFixed(1)},${dy.toFixed(1)}`);

    // Label position slightly outside
    const lx = cx + Math.cos(angle) * (radius + 24);
    const ly = cy + Math.sin(angle) * (radius + 24);
    const textAnchor = Math.abs(Math.cos(angle)) < 0.2 ? 'middle' : Math.cos(angle) > 0 ? 'start' : 'end';
    out += `<text x="${lx.toFixed(1)}" y="${(ly + 4).toFixed(1)}" text-anchor="${textAnchor}" class="radar-label">${escapeAttr(d.name)}</text>`;
  });

  // Data area polygon
  out += `<polygon points="${dataPoints.join(' ')}" class="radar-area"/>`;

  // Data dots
  dataPoints.forEach((pt) => {
    const [x, y] = pt.split(',');
    out += `<circle cx="${x}" cy="${y}" r="4.5" class="radar-dot"/>`;
  });

  return wrapSvg(out, `0 0 ${size} ${size}`, 'iq-svg-radar', 'Cognitive Ability Profile Radar Chart');
}
